import Vue from "vue";
import * as Sentry from "@sentry/vue";
import axios, { AxiosError } from "axios";
import moduleConstant from "../common/module-constant";

export type SentryCaptureContext = any;

interface PendingSentryError {
    error: Error;
    context?: SentryCaptureContext;
}

type NotificationPayload = Record<string, any>;

const pendingSentryErrors: PendingSentryError[] = [];
let globalSentryHandlersBound = false;
let sentryInitialized = false;

export const SENTRY_REPORTING_ENVIRONMENT = "production";

type StoredAuthorizationData = {
    environment?: unknown;
    data?: { information?: { environment?: unknown } };
};

// Internal QA tenants (qa5web.omnicasa.com, *.omnicasa.com) run the
// release build without an auth environment; only the host tells them apart.
const INTERNAL_HOST = /^qaf?\d[a-z0-9]*web\.|\.omnicasastaging\.be$/i;

// A release build served from a dev machine (static server, preview) is not the production site.
const LOCAL_HOST = /^(?:localhost|127\.0\.0\.1)$|\.local$/i;

// The login host serves dev/staging builds from https://{host}/{env}/{version}/
// and release builds from https://{host}/{version}/, and announces that root in
// <meta name="Base-Static-Url">, before any login. Only the non-production
// environments the host knows count, so an unexpected path prefix cannot switch
// production off.
const SERVED_ENVIRONMENT = /^(?:[a-z]+:)?\/\/[^/]+\/(dev|development|stage|staging)\/[^/]+\/?$/i;

// Auth environment "release" is production; dev/staging pass through.
// Normal logins store the environment at the root; webview sessions store the
// /webappinfo body, which only carries it under data.information.
// Only dev/staging logins carry an auth environment: production logins carry
// none. Without one, the served build's environment decides; a release build
// (NODE_ENV=production) is the production site unless it runs on an internal
// QA host. A local host (localhost, 127.0.0.1, *.local) resolves to development.
export function resolveSentryEnvironment(
    authData: StoredAuthorizationData | null | undefined,
    hostname: string = window.location.hostname,
    staticRootUrl: string = document.querySelector("meta[name='Base-Static-Url']")?.getAttribute("content") || ""
): string {
    const rawEnvironment = (
        authData?.environment ||
        authData?.data?.information?.environment ||
        staticRootUrl.match(SERVED_ENVIRONMENT)?.[1] ||
        ""
    )
        .toString()
        .toLowerCase();
    if (rawEnvironment) {
        return rawEnvironment === "release" ? "production" : rawEnvironment;
    }
    if (INTERNAL_HOST.test(hostname)) {
        return "internal";
    }
    if (LOCAL_HOST.test(hostname)) {
        return "development";
    }

    const runtimeEnvironment =
        typeof process !== "undefined" && process.env && process.env.NODE_ENV
            ? process.env.NODE_ENV.toLowerCase()
            : undefined;
    return runtimeEnvironment || "unknown";
}

function normalizeError(error: any): Error {
    if (error instanceof Error) {
        return error;
    }

    if (typeof error === "string") {
        return new Error(error);
    }

    try {
        const serialized =
            error && typeof error === "object" ? JSON.stringify(error) : String(error);
        return new Error(serialized);
    } catch {
        return new Error(String(error));
    }
}

const CAPTURED_FLAG = "__sentryCaptured";

// One error object = one Sentry event, no matter how many layers see it
// (console wrapper, toast wrapper, explicit $captureError). Marking lives on
// the original object so it survives normalizeError's copies.
function isCaptured(error: any): boolean {
    return !!(error && typeof error === "object" && error[CAPTURED_FLAG]);
}

export function markCaptured(error: any): void {
    if (!error || typeof error !== "object") {
        return;
    }
    try {
        Object.defineProperty(error, CAPTURED_FLAG, { value: true });
    } catch {
        // frozen object â€” worst case is a duplicate event, never a crash
    }
}

// The client lost the request (offline, timeout, page left mid-request): not an
// app error. Services resolve the AxiosError and reject with its bare message,
// so the exact axios messages count too. Config errors (no response, other codes) still report.
const TRANSPORT_FAILURE_CODES: string[] = [AxiosError.ERR_NETWORK, AxiosError.ECONNABORTED, AxiosError.ETIMEDOUT];
const TRANSPORT_FAILURE_MESSAGE = /^(?:Network Error|Request aborted|timeout(?: of \d+ms)? exceeded)$/;

export function isTransportFailure(error: unknown): boolean {
    if (axios.isAxiosError(error)) {
        return !error.response && TRANSPORT_FAILURE_CODES.includes(error.code ?? "");
    }
    const message = error instanceof Error ? error.message : error;
    return typeof message === "string" && TRANSPORT_FAILURE_MESSAGE.test(message);
}

export function captureAppError(
    error: any,
    context?: SentryCaptureContext
): void {
    if (!error) {
        return;
    }

    // Cancelled requests were superseded on purpose (e.g. a newer heritage check);
    // transport failures are client network drops (VUE-183).
    if (axios.isCancel(error) || isTransportFailure(error)) {
        return;
    }

    if (isCaptured(error)) {
        return;
    }
    markCaptured(error);

    const normalizedError = normalizeError(error);
    const client = Sentry.getClient();

    if (!client || !sentryInitialized) {
        if (pendingSentryErrors.length >= 50) {
            pendingSentryErrors.shift();
        }

        pendingSentryErrors.push({ error: normalizedError, context });
        return;
    }

    Sentry.captureException(normalizedError, context);
}

// A concurrency conflict is an expected outcome: the save flows show the
// conflict modal. Known modules leave only a breadcrumb for later errors and
// replays; an unmapped module means a caller passed a bad payload.
export function reportConcurrencyConflict(
    payload: { module?: unknown; id?: unknown } | null | undefined
): void {
    if (!payload || !payload.module) {
        return;
    }
    const moduleName = Object.entries(moduleConstant).find(([, value]) => value === payload.module)?.[0];
    if (!moduleName) {
        captureAppError(new Error("Unexpected concurrency conflict payload"), {
            tags: {
                origin: "store-conflict",
                module: String(payload.module),
                reason: "unexpected-conflict-payload"
            },
            extra: { id: payload.id }
        });
        return;
    }
    Sentry.addBreadcrumb({
        category: "conflict",
        level: "info",
        message: "Concurrency conflict",
        data: { module: moduleName, id: payload.id }
    });
}

export function flushPendingSentryErrors(): void {
    if (!pendingSentryErrors.length) {
        return;
    }

    while (pendingSentryErrors.length) {
        const entry = pendingSentryErrors.shift();
        if (!entry) {
            continue;
        }

        Sentry.captureException(entry.error, entry.context);
    }
}

export function hasSentryInitialized(): boolean {
    return sentryInitialized;
}

export function markSentryInitialized(): void {
    sentryInitialized = true;
    flushPendingSentryErrors();
}

function resolveComponentName(instance: Vue | undefined): string | undefined {
    if (!instance) {
        return undefined;
    }

    const options = instance.$options as Record<string, any>;
    return options?.name || options?._componentTag || options?._parentVnode?.tag;
}

function buildDefaultTags(vm: Vue | undefined): Record<string, string> {
    const tags: Record<string, string> = {};

    const component = resolveComponentName(vm);
    if (component) {
        tags.component = component;
    }

    const route = vm?.$route?.name;
    if (route) {
        tags.route = route;
    }

    return tags;
}

function withDefaultTags(
    vm: Vue | undefined,
    context?: SentryCaptureContext
): SentryCaptureContext {
    return {
        ...(context || {}),
        tags: { ...buildDefaultTags(vm), ...(context?.tags || {}) }
    };
}

function shouldCaptureNotification(payload: NotificationPayload): boolean {
    if (!payload) {
        return false;
    }

    if (payload.skipSentry === true) {
        return false;
    }

    if (payload.capture === true) {
        return true;
    }

    // Toasts are user feedback, not error reports. String toasts are
    // validation / "please select" / duplicate-check feedback, and server
    // failures are already captured once in axios-helper (origin: api).
    // Only a real Error handed to the toast is an application bug.
    const type = (payload.type || "").toLowerCase();
    return (type === "error" || type === "danger") && payload.error instanceof Error;
}

function buildNotificationContext(
    payload: NotificationPayload,
    vm: Vue | undefined,
    origin: string = "notification"
): SentryCaptureContext {
    const tags = {
        origin,
        notifyType: payload.type || "info",
        ...buildDefaultTags(vm),
        ...(payload.tags || {})
    };

    const extra = {
        title: payload.title,
        message: payload.message,
        duration: payload.duration,
        ...((payload.extra as Record<string, any>) || {})
    };

    const customContext = payload.sentryContext as SentryCaptureContext | undefined;
    if (customContext) {
        return {
            ...customContext,
            tags: { ...tags, ...(customContext.tags || {}) },
            extra: { ...extra, ...(customContext.extra || {}) }
        };
    }

    return { tags, extra };
}

function createErrorFromNotification(payload: NotificationPayload): Error {
    if (payload.error) {
        return normalizeError(payload.error);
    }

    const message =
        payload.message ||
        payload.title ||
        "Unhandled notification error";

    const error = new Error(
        typeof message === "string" ? message : JSON.stringify(message)
    );

    error.name = payload.errorName || "NotificationError";

    return error;
}

function sanitizeNotificationOptions(
    payload: NotificationPayload
): NotificationPayload {
    const sanitized = { ...payload };
    delete sanitized.sentryContext;
    delete sanitized.capture;
    delete sanitized.skipSentry;
    delete sanitized.errorName;
    delete sanitized.error;
    delete sanitized.extra;
    delete sanitized.tags;
    return sanitized;
}

function normalizeNotificationInput(input: any): NotificationPayload {
    if (typeof input === "string") {
        return { message: input };
    }

    return { ...(input || {}) };
}

function wrapNotification(VueInstance: typeof Vue): void {
    const originalNotify = VueInstance.prototype.$notify;

    if (typeof originalNotify !== "function") {
        return;
    }

    if ((originalNotify as any).__sentryWrapped === true) {
        return;
    }

    const wrapped = function thisNotify(this: Vue, input: any) {
        const normalized = normalizeNotificationInput(input);

        if (!normalized.type && typeof input === "string") {
            normalized.type = "info";
        }

        const optionsForElement = sanitizeNotificationOptions(normalized);
        const result = originalNotify.call(this, optionsForElement as any);

        if (shouldCaptureNotification(normalized) && !isCaptured(normalized.error)) {
            const error = createErrorFromNotification(normalized);
            const context = buildNotificationContext(normalized, this);
            captureAppError(error, context);
            // Mark AFTER capturing: when normalized.error is an Error,
            // createErrorFromNotification returns the same reference, and
            // pre-marking would make captureAppError skip it entirely. For
            // plain objects this marks the original so the console wrapper
            // does not report it a second time.
            markCaptured(normalized.error);
        }

        return result;
    } as typeof Vue.prototype.$notify;

    const shortcuts = ["success", "warning", "info", "error"];
    shortcuts.forEach(type => {
        const originalShortcut = (originalNotify as any)[type];
        if (typeof originalShortcut === "function") {
            (wrapped as any)[type] = function notifyShortcut(this: Vue, input: any) {
                const normalized = normalizeNotificationInput(input);
                normalized.type = normalized.type || type;
                return wrapped.call(this, normalized);
            };
        }
    });

    ["close", "closeAll"].forEach(method => {
        if (typeof (originalNotify as any)[method] === "function") {
            (wrapped as any)[method] = (originalNotify as any)[method].bind(originalNotify);
        }
    });

    (wrapped as any).__sentryWrapped = true;

    VueInstance.prototype.$notify = wrapped;
}

function wrapMessage(VueInstance: typeof Vue): void {
    const originalMessage = VueInstance.prototype.$message;

    if (typeof originalMessage !== "function") {
        return;
    }

    if ((originalMessage as any).__sentryWrapped === true) {
        return;
    }

    // Element UI renders nothing for a raw Error/exotic input, so map it to
    // its message and keep the original on `error` for the Sentry capture.
    const normalizeMessageInput = (input: any): NotificationPayload =>
        input instanceof Error
            ? { message: input.message, error: input }
            : normalizeNotificationInput(input);

    const wrapped = function thisMessage(this: Vue, input: any) {
        const normalized = normalizeMessageInput(input);

        if (!normalized.type) {
            if (input instanceof Error) {
                normalized.type = "error";
            } else if (typeof input === "string") {
                normalized.type = "info";
            }
        }

        const optionsForElement = sanitizeNotificationOptions(normalized);
        const result = originalMessage.call(this, optionsForElement as any);

        if (shouldCaptureNotification(normalized) && !isCaptured(normalized.error)) {
            const error = createErrorFromNotification(normalized);
            const context = buildNotificationContext(normalized, this, "message");
            captureAppError(error, context);
            // Mark AFTER capturing â€” see the matching note in wrapNotification.
            markCaptured(normalized.error);
        }

        return result;
    } as any;

    const shortcuts = ["success", "warning", "info", "error"];
    shortcuts.forEach(type => {
        if (typeof (originalMessage as any)[type] === "function") {
            wrapped[type] = function messageShortcut(this: Vue, input: any) {
                const normalized = normalizeMessageInput(input);
                normalized.type = normalized.type || type;
                return wrapped.call(this, normalized);
            };
        }
    });

    ["close", "closeAll"].forEach(method => {
        if (typeof (originalMessage as any)[method] === "function") {
            wrapped[method] = (originalMessage as any)[method].bind(originalMessage);
        }
    });

    wrapped.__sentryWrapped = true;

    VueInstance.prototype.$message = wrapped;
}

export interface NotifyErrorOptions extends NotificationPayload {
    sentryContext?: SentryCaptureContext;
}

function createNotifyError(VueInstance: typeof Vue): void {
    VueInstance.prototype.$notifyError = function notifyError(
        this: Vue,
        error: any,
        options?: NotifyErrorOptions | string
    ) {
        const normalized = normalizeNotificationInput(options);

        normalized.type = normalized.type || "error";
        normalized.skipSentry = true;
        normalized.error = error;

        const context =
            normalized.sentryContext ||
            buildNotificationContext(normalized, this);

        captureAppError(error, context);

        // Pass the normalized payload (with skipSentry) so the wrapped $notify
        // sees the flag and does not re-capture. The wrapper sanitizes before
        // forwarding to ElementUI, so the flag never leaks downstream.
        return this.$notify(normalized as any);
    };
}

export function installErrorReporter(VueInstance: typeof Vue): void {
    wrapNotification(VueInstance);
    wrapMessage(VueInstance);
    createNotifyError(VueInstance);

    VueInstance.prototype.$captureError = function captureFromVue(
        this: Vue,
        error: any,
        context?: SentryCaptureContext
    ) {
        captureAppError(error, withDefaultTags(this, context));
    };

    // Error toast for EXPECTED user mistakes (nothing selected, missing
    // required input, ...) â€” shown like $message.error but never captured.
    VueInstance.prototype.$messageUserError = function messageUserError(
        this: Vue,
        message: any
    ) {
        return this.$message({
            message,
            type: "error",
            skipSentry: true
        } as any);
    };
}

let capturingFromConsole = false;

function wrapConsoleMethod(level: "error" | "log"): void {
    const original = console[level].bind(console);

    console[level] = (...args: any[]) => {
        original(...args);

        if (capturingFromConsole) {
            return;
        }
        // Vue dev-build warnings go through console.error; they are lint
        // noise, not application errors.
        if (typeof args[0] === "string" && args[0].startsWith("[Vue warn]")) {
            return;
        }

        const errorArg = args.find(arg => arg instanceof Error);
        // console.log is everyday debug output â€” only a real Error instance
        // signals a swallowed catch. console.error is treated as an error
        // report even when called with plain strings/objects.
        if (level === "log" && !errorArg) {
            return;
        }
        if (!args.length) {
            return;
        }

        // Defer one microtask: when the same catch block also calls
        // $captureError / shows an error toast, that richer capture runs
        // first, marks the error, and this one becomes a no-op.
        Promise.resolve().then(() => {
            if (errorArg && isCaptured(errorArg)) {
                return;
            }
            const consoleMessage = args
                .map(arg => (typeof arg === "string" ? arg : undefined))
                .filter(Boolean)
                .join(" ");
            capturingFromConsole = true;
            try {
                captureAppError(errorArg || args[0], {
                    tags: { origin: `console.${level}` },
                    extra: { consoleMessage }
                });
            } finally {
                capturingFromConsole = false;
            }
        });
    };
}

// Sentry's default `globalHandlersIntegration` (auto-enabled in @sentry/vue
// v8) already captures `window.error` and `unhandledrejection`; registering
// our own listeners duplicated events, so only the console wrappers are
// installed here. They are the central net for catch blocks that swallow
// errors with console.error / console.log(err) â€” no per-call-site edits.
export function setupGlobalSentryHandlers(): void {
    if (globalSentryHandlersBound) {
        return;
    }
    globalSentryHandlersBound = true;

    wrapConsoleMethod("error");
    wrapConsoleMethod("log");
}

declare module "vue/types/vue" {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface Vue {
        $captureError: (error: any, context?: SentryCaptureContext) => void;
        $messageUserError: (message: any) => any;
        $notifyError: (
            error: any,
            options?: NotifyErrorOptions | string
        ) => void;
    }
}
